import React, {useState} from 'react';

import {Button, Checkmark, Collapse} from '@kit';

import {WeaponProperty} from '../../../../generated/types';
import {cls} from '../../../../components/utils';
import {useCharacter} from '../character-context';
import {
	equipEntries,
	EquippedItem,
	EquipmentStack,
	hasProperty,
	isEquippable,
	isRangedWeapon,
	isWeapon,
	setOffHand,
	stackEquipment,
} from '../model/equipment';
import {itemEffectSummary} from '../model/item-summary';
import {
	itemSubTypeLabel,
	itemTypeLabel,
	weaponCategoryLabel,
	weaponMasteryLabel,
	weaponPropertyLabel,
} from '../model/labels';
import {byTitle, formatPounds, formatRange} from './format';
import {ExpandButton, expandOnClick} from './expandable-row';
import {IdentifyItemModal} from './identify/identify-item-modal';
import {ItemDescription} from './item-description';
import {AttunementMark} from './spell-title-icons';

import css from './sheet.module.css';

export function useEquipmentActions() {
	const {character, update} = useCharacter();

	const setEquipped = (entryKeys: string[], equipped: boolean) =>
		update((current) => ({equipment: equipEntries(current.equipment, character.equipment, entryKeys, equipped)}));

	const setStackOffHand = (stack: EquipmentStack, offHand: boolean) =>
		update((current) => ({equipment: setOffHand(current.equipment, stack, offHand)}));

	return {setEquipped, setStackOffHand};
}

export const EquipmentTable: React.FC<{items: EquippedItem[]}> = ({items}) => (
	<div className={css.tableScroll}>
		<table className={`${css.actionTable} ${css.equipmentTable}`}>
			<thead>
				<tr>
					<th className={css.checkCell} data-col="check">
						<span className={css.visuallyHidden}>Используется</span>
					</th>
					<th>Название</th>
					<th data-col="weight">Вес</th>
					<th data-col="price">Цена, ЗМ</th>
					<th data-col="notes">Примечания</th>
				</tr>
			</thead>
			<tbody>
				{stackEquipment(items)
					.sort((first, second) => byTitle(first.item.title, second.item.title))
					.map((stack) => (
						<EquipmentTableRow key={stack.key} stack={stack} />
					))}
			</tbody>
		</table>
	</div>
);

const EquipmentTableRow: React.FC<{stack: EquipmentStack}> = ({stack}) => {
	const {setEquipped} = useEquipmentActions();
	const [isIdentifying, setIdentifying] = useState(false);
	const [isOpen, setOpen] = useState(false);
	const equipped = stack.entries[0];
	const {entry, item, isAttuned, isWorn} = equipped;
	const {quantity} = stack;
	const canEquip = isEquippable(item);
	const effects = itemEffectSummary(item);
	const subTypes = item.subTypes.map((subType) => itemSubTypeLabel(subType).toLowerCase()).join(', ');
	const kind = [item.type && itemTypeLabel(item.type), subTypes && `(${subTypes})`].filter(Boolean).join(' ');
	const hasRange =
		isWeapon(item) && !!item.range && (isRangedWeapon(item) || hasProperty(item, WeaponProperty.Thrown));
	const notes = [
		entry.offHand && 'во второй руке',
		item.weaponCategory && `${weaponCategoryLabel(item.weaponCategory)} оружие`,
		hasRange && `дистанция ${formatRange(item.range!)}`,
		...item.weaponProperties.map(weaponPropertyLabel),
		...item.weaponMastery.map((mastery) => `мастерство: ${weaponMasteryLabel(mastery)}`),
		item.armorKind && (item.armorKind === 'Shield' ? 'Щит' : `${itemSubTypeLabel(item.armorKind)} броня`),
		item.armorClass != null && `КБ ${item.armorKind === 'Shield' ? '+' : ''}${item.armorClass}`,
		item.strength && `Сила ${item.strength}`,
		item.stealthDisadvantage && 'Помеха к Скрытности',
	].filter(Boolean);

	return (
		<>
			<tr className={css.actionRow}>
				<td className={css.checkCell}>
					{canEquip && (
						<Checkmark
							checked={!!entry.equipped}
							aria-label={`Используется: ${item.title}`}
							onChange={() =>
								setEquipped(
									stack.entries.map(({entry}) => entry.key),
									!entry.equipped,
								)
							}
						/>
					)}
				</td>
				<td
					className={cls(css.nameCell, item.hasDescription && css.expandable)}
					onClick={item.hasDescription ? expandOnClick(() => setOpen(!isOpen)) : undefined}
				>
					<span className={css.nameCellContent}>
						{item.hasDescription ? (
							<ExpandButton isOpen={isOpen} onToggle={() => setOpen(!isOpen)} />
						) : (
							<span className={css.expandPlaceholder} />
						)}
						<span className={css.equipmentName}>
							<span className={css.rowTitle}>
								{item.title}
								{item.baseTitle && <span className={css.muted}> ({item.baseTitle})</span>}
								{quantity > 1 && <span className={css.muted}> × {quantity}</span>}
								{item.requiresAttunement && <AttunementMark attuned={isAttuned} />}
							</span>
							{kind && <span className={css.cellNote}>{kind}</span>}
							{!item.isMagic && (
								<Button size="small" appearance="subtle" onClick={() => setIdentifying(true)}>
									Опознать
								</Button>
							)}
							{isIdentifying && (
								<IdentifyItemModal equipped={equipped} onClose={() => setIdentifying(false)} />
							)}
						</span>
					</span>
				</td>
				<td className={css.nowrap}>{item.weight > 0 && formatPounds(item.weight * quantity)}</td>
				<td className={css.nowrap}>
					{item.priceInGold != null && (item.priceInGold * quantity).toLocaleString('ru')}
				</td>
				<td className={css.notesCell}>
					<span className={css.equipmentNotes}>
						{notes.length > 0 && <span>{notes.join(', ')}</span>}
						{effects.equipped.length > 0 && (
							<span className={cls(!isWorn && css.muted)}>
								{item.requiresAttunement && 'Надет: '}
								{effects.equipped.join('; ')}
							</span>
						)}
						{effects.attuned.length > 0 && (
							<span className={cls(!isAttuned && css.muted)}>
								При настройке: {effects.attuned.join('; ')}
							</span>
						)}
					</span>
				</td>
			</tr>
			{item.hasDescription && (
				<Collapse
					open={isOpen}
					wrapper={(content) => (
						<tr>
							<td colSpan={5} className={css.descriptionCell}>
								{content}
							</td>
						</tr>
					)}
				>
					<ItemDescription itemId={entry.itemId} />
				</Collapse>
			)}
		</>
	);
};
